import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { showGlobalNotification } from '../hooks/useNotifications';
import { apiService } from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import {
  Plus, Search, Edit2, Trash2, Server, AlertTriangle,
  CheckCircle, Clock, Cpu, MemoryStick, HardDrive,
  Eye, Copy, RefreshCw, Download, FileText
} from 'lucide-react';
// import { physicalServers, companies, locations, allUsers } from '../data/mockData';
import { PhysicalServer } from '../types';
import Modal from './Modal';
import { useGlobalSearch } from '../hooks/useGlobalSearch';
import ServerUpdateCheckModal from './ServerUpdateCheckModal';

const PhysicalServersPage: React.FC = () => {
  useAuth();
  const { t } = useLanguage();
  const [searchTerm, setSearchTerm] = useState('');
  useGlobalSearch('physical-servers', setSearchTerm);
  const [showModal, setShowModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [editingServer, setEditingServer] = useState<PhysicalServer | null>(null);
  const [selectedServer, setSelectedServer] = useState<PhysicalServer | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterCompany, setFilterCompany] = useState<number>(0);
  const [filterLocation, setFilterLocation] = useState<number>(0);
  const [filterBrand, setFilterBrand] = useState<string>('all');
  const [filterServerType, setFilterServerType] = useState<string>('all');
  const [serversData, setServersData] = useState<PhysicalServer[]>([]);
  const [companiesData, setCompaniesData] = useState<any[]>([]);
  const [locationsData, setLocationsData] = useState<any[]>([]);
  const [usersData, setUsersData] = useState<any[]>([]);
  const [virtualServersData, setVirtualServersData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showUpdateCheckModal, setShowUpdateCheckModal] = useState(false);
  const [updateCheckServer, setUpdateCheckServer] = useState<PhysicalServer | null>(null);
  
  const [serverData, setServerData] = useState({
    server_name: '',
    hostname: '',
    ip_address: '',
    server_type: 'rack' as PhysicalServer['server_type'],
    brand: '',
    model: '',
    serial_number: '',
    processor: '',
    cpu_cores: 16,
    ram_gb: 16,
    storage_gb: 1000,
    operating_system: '',
    location_id: 0,
    company_id: 0,
    rack_position: '',
    power_consumption_watts: 0,
    warranty_end_date: '',
    purchase_date: '',
    purchase_price: 0,
    currency: 'TRY' as 'TRY' | 'USD' | 'EUR',
    status: 'active' as PhysicalServer['status'],
    assigned_admin_id: 0,
    notes: ''
  });

  // Load data on component mount
  React.useEffect(() => {
    loadData();
  }, []);

  // Bildirimden belirli bir sunucu açma
  React.useEffect(() => {
    const handler = async (e: CustomEvent) => {
      if (e.detail?.page !== 'physical-servers' || !e.detail?.entityId) return;
      try {
        const server = await apiService.getPhysicalServerById(e.detail.entityId);
        if (server) { setSelectedServer(server); setShowDetailModal(true); }
      } catch (err) { console.error('[openEntityById physicalservers]', err); }
    };
    window.addEventListener('openEntityById', handler as EventListener);
    return () => window.removeEventListener('openEntityById', handler as EventListener);
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [serversResponse, companiesResponse, locationsResponse, usersResponse, virtualServersResponse] = await Promise.all([
        apiService.getPhysicalServers(),
        apiService.getCompanies(),
        apiService.getLocations(),
        apiService.getUsers(),
        apiService.getVirtualServers()
      ]);

      // API response format kontrolü
      setServersData(Array.isArray(serversResponse) ? serversResponse : (serversResponse?.data || []));
      setCompaniesData(Array.isArray(companiesResponse) ? companiesResponse : (companiesResponse?.data || []));
      setLocationsData(Array.isArray(locationsResponse) ? locationsResponse : (locationsResponse?.data || []));
      setUsersData(Array.isArray(usersResponse) ? usersResponse.filter(u => u.role === 'admin' || u.role === 'superadmin') : (usersResponse?.data?.filter(u => u.role === 'admin' || u.role === 'superadmin') || []));
      setVirtualServersData(Array.isArray(virtualServersResponse) ? virtualServersResponse : (virtualServersResponse?.data || []));

    } catch (error) {
      console.error('Error loading data:', error);
      showGlobalNotification.error(t('common.error'), t('physerver.errorLoad'));
    } finally {
      setLoading(false);
    }
  };

  const statusOptions = [
    { value: 'active', label: t('physerver.statusActive'), color: 'bg-green-100 text-green-800' },
    { value: 'maintenance', label: t('physerver.statusMaintenance'), color: 'bg-yellow-100 text-yellow-800' },
    { value: 'offline', label: t('physerver.statusOffline'), color: 'bg-red-100 text-red-800' },
    { value: 'decommissioned', label: t('physerver.statusDecommissioned'), color: 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200' }
  ];

  const serverTypeOptions = [
    { value: 'rack', label: 'Rack Mount' },
    { value: 'blade', label: 'Blade Server' },
    { value: 'tower', label: 'Tower' }
  ];

  const uniqueBrands = Array.from(new Set(serversData.map(s => s.brand).filter(Boolean))).sort();

  const filteredServers = serversData.filter(server => {
    const matchesSearch = (server.server_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (server.hostname || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (server.ip_address || '').includes(searchTerm) ||
                         (server.operating_system || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (server.brand || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (server.model || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || server.status === filterStatus;
    const matchesCompany = filterCompany === 0 || server.company_id === filterCompany;
    const matchesLocation = filterLocation === 0 || server.location_id === filterLocation;
    const matchesBrand = filterBrand === 'all' || server.brand === filterBrand;
    const matchesServerType = filterServerType === 'all' || server.server_type === filterServerType;

    return matchesSearch && matchesStatus && matchesCompany && matchesLocation && matchesBrand && matchesServerType;
  });

  const handleEdit = (server: PhysicalServer) => {
    setEditingServer(server);

    // ISO formatındaki tarihleri yyyy-MM-dd formatına çevir
    const formatDateForInput = (dateString: string) => {
      if (!dateString) return '';
      return dateString.split('T')[0]; // ISO format: 2025-12-17T21:00:00.000Z -> 2025-12-17
    };

    setServerData({
      server_name: server.server_name,
      hostname: server.hostname,
      ip_address: server.ip_address,
      server_type: server.server_type,
      brand: server.brand,
      model: server.model,
      serial_number: server.serial_number,
      processor: server.processor,
      cpu_cores: server.cpu_cores || 16,
      ram_gb: server.ram_gb,
      storage_gb: server.storage_gb,
      operating_system: server.operating_system,
      location_id: server.location_id,
      company_id: server.company_id,
      rack_position: server.rack_position || '',
      power_consumption_watts: server.power_consumption_watts || 0,
      warranty_end_date: formatDateForInput(server.warranty_end_date || ''),
      purchase_date: formatDateForInput(server.purchase_date),
      purchase_price: server.purchase_price || 0,
      currency: server.currency || 'TRY',
      status: server.status,
      assigned_admin_id: server.assigned_admin_id || 0,
      notes: server.notes || ''
    });
    setShowModal(true);
  };

  const handleDuplicate = (server: PhysicalServer) => {
    setEditingServer(null);
    setServerData({
      server_name: `${server.server_name} ${t('physerver.duplicateSuffix')}`,
      hostname: '',
      ip_address: '',
      server_type: server.server_type,
      brand: server.brand,
      model: server.model,
      serial_number: '',
      processor: server.processor,
      cpu_cores: server.cpu_cores || 16,
      ram_gb: server.ram_gb,
      storage_gb: server.storage_gb,
      operating_system: server.operating_system,
      location_id: server.location_id,
      company_id: server.company_id,
      rack_position: '',
      power_consumption_watts: server.power_consumption_watts || 0,
      warranty_end_date: '',
      purchase_date: '',
      purchase_price: server.purchase_price || 0,
      currency: server.currency || 'TRY',
      status: 'active',
      assigned_admin_id: server.assigned_admin_id || 0,
      notes: server.notes || ''
    });
    setShowModal(true);
  };

  const handleAdd = () => {
    setEditingServer(null);
    setServerData({
      server_name: '',
      hostname: '',
      ip_address: '',
      server_type: 'rack',
      brand: '',
      model: '',
      serial_number: '',
      processor: '',
      cpu_cores: 16,
      ram_gb: 16,
      storage_gb: 1000,
      operating_system: '',
      location_id: 0,
      company_id: 0,
      rack_position: '',
      power_consumption_watts: 0,
      warranty_end_date: '',
      purchase_date: '',
      purchase_price: 0,
      currency: 'TRY',
      status: 'active',
      assigned_admin_id: 0,
      notes: ''
    });
    setShowModal(true);
  };

  const validateIPAddress = (ip: string): boolean => {
    const ipRegex = /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
    return ipRegex.test(ip);
  };

  const handleSave = async () => {
    if (!serverData.server_name || !serverData.hostname || !serverData.ip_address ||
        !serverData.serial_number || !serverData.brand || !serverData.model ||
        !serverData.processor || !serverData.operating_system ||
        !serverData.company_id || !serverData.location_id || !serverData.purchase_date) {
      showGlobalNotification.error(t('common.error'), t('physerver.errorRequiredFields'));
      return;
    }

    // IP adresi geçerlilik kontrolü
    if (!validateIPAddress(serverData.ip_address)) {
      showGlobalNotification.error(t('common.error'), t('physerver.errorInvalidIP'));
      return;
    }

    // Hostname duplicate kontrolü (düzenleme yapılıyorsa kendi kaydını hariç tut)
    const duplicateHostname = serversData.find(server => {
      // Eğer düzenleme modundaysak ve bu kayıt düzenlenen kayıtsa, atla
      if (editingServer && Number(server.id) === Number(editingServer.id)) {
        console.log('Skipping own record:', server.id, 'vs', editingServer.id);
        return false;
      }
      // Hostname karşılaştır
      const matches = server.hostname.toLowerCase() === serverData.hostname.toLowerCase();
      if (matches) {
        console.log('Hostname match found:', server.hostname, 'Server ID:', server.id, 'Editing ID:', editingServer?.id);
      }
      return matches;
    });
    if (duplicateHostname) {
      console.error('Duplicate hostname detected:', {
        duplicateServer: duplicateHostname.server_name,
        duplicateId: duplicateHostname.id,
        editingServer: editingServer?.server_name,
        editingId: editingServer?.id
      });
      showGlobalNotification.error(t('common.error'), t('physerver.errorDuplicateHostname', { name: duplicateHostname.server_name }));
      return;
    }

    // IP adresi duplicate kontrolü (düzenleme yapılıyorsa kendi kaydını hariç tut)
    const duplicateIP = serversData.find(server => {
      // Eğer düzenleme modundaysak ve bu kayıt düzenlenen kayıtsa, atla
      if (editingServer && Number(server.id) === Number(editingServer.id)) {
        return false;
      }
      // IP karşılaştır
      return server.ip_address === serverData.ip_address;
    });
    if (duplicateIP) {
      showGlobalNotification.error(t('common.error'), t('physerver.errorDuplicateIP', { name: duplicateIP.server_name, ip: duplicateIP.ip_address }));
      return;
    }

    try {
      // assigned_admin_id'yi null yap eğer 0 ise, next_maintenance'ı null yap
      const dataToSend = {
        ...serverData,
        assigned_admin_id: serverData.assigned_admin_id === 0 ? null : serverData.assigned_admin_id,
        next_maintenance: null
      };


      if (editingServer) {
        // Update existing server
        await apiService.updatePhysicalServer(editingServer.id, dataToSend);
        showGlobalNotification.success(t('common.success'), t('physerver.successUpdated'));
      } else {
        // Add new server
        await apiService.createPhysicalServer(dataToSend);
        showGlobalNotification.success(t('common.success'), t('physerver.successCreated'));
      }

      // Reload data
      await loadData();
      setShowModal(false);
    } catch (error: any) {
      console.error('Error saving physical server:', error);
      showGlobalNotification.error(t('common.error'), error.message || t('physerver.errorSave'));
    }
  };

  const handleDelete = async (id: number) => {
    const serverToDelete = serversData.find(s => s.id === id);
    if (!serverToDelete) return;
    
    if (confirm(t('physerver.confirmDelete'))) {
      try {
        await apiService.deletePhysicalServer(id);
        await loadData();
        showGlobalNotification.success(t('common.success'), t('physerver.successDeleted'));
      } catch (error: any) {
        console.error('Error deleting physical server:', error);
        showGlobalNotification.error(t('common.error'), error.message || t('physerver.errorDelete'));
      }
    }
  };

  const exportToExcel = () => {
    try {
      const locale = 'tr-TR';
      const exportData = filteredServers.map((server, i) => ({
        '#': i + 1,
        'Sunucu Adı': server.server_name,
        'Hostname': server.hostname,
        'IP Adresi': server.ip_address,
        'Tür': server.server_type || '',
        'Marka': server.brand,
        'Model': server.model,
        'Seri No': server.serial_number,
        'İşletim Sistemi': server.operating_system,
        'İşlemci': server.processor || '',
        'CPU Çekirdek': server.cpu_cores || '',
        'RAM (GB)': server.ram_gb,
        'Disk (GB)': server.storage_gb,
        'Şirket': (server as any).company_name || '',
        'Konum': (server as any).location_name || '',
        'Raf Pozisyonu': server.rack_position || '',
        'Durum': statusOptions.find(s => s.value === server.status)?.label || server.status,
        'Garanti Bitiş': server.warranty_end_date ? new Date(server.warranty_end_date).toLocaleDateString(locale) : '',
        'Satın Alma Tarihi': server.purchase_date ? new Date(server.purchase_date).toLocaleDateString(locale) : '',
      }));
      const ws = XLSX.utils.json_to_sheet(exportData);
      ws['!cols'] = [
        {wch:5},{wch:20},{wch:18},{wch:16},{wch:8},{wch:14},{wch:16},
        {wch:16},{wch:20},{wch:22},{wch:12},{wch:10},{wch:10},
        {wch:18},{wch:16},{wch:14},{wch:12},{wch:14},{wch:14}
      ];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, t('physerver.excelSheet'));
      XLSX.writeFile(wb, `${t('physerver.excelFilename')}-${new Date().toISOString().split('T')[0]}.xlsx`);
      showGlobalNotification.success(t('common.success'), `${filteredServers.length} ${t('physerver.exportExcelSuccess')}`);
    } catch (err) {
      showGlobalNotification.error(t('common.error'), t('physerver.exportExcelError'));
    }
  };

  const exportToPDF = () => {
    try {
      const locale = 'tr-TR';
      const pdf = new jsPDF('l', 'mm', 'a4');
      pdf.setFontSize(16);
      pdf.text(t('physerver.pdfTitle'), 14, 15);
      pdf.setFontSize(10);
      pdf.text(`${t('physerver.pdfReportDate')} ${new Date().toLocaleDateString(locale)}`, 14, 23);
      pdf.text(`${t('physerver.pdfTotal')} ${filteredServers.length}`, 200, 23);
      (pdf as any).autoTable({
        head: [['#', 'Sunucu Adı', 'Hostname', 'IP Adresi', 'Marka/Model', 'İşl. Sistemi', 'CPU', 'RAM', 'Disk', 'Şirket', 'Konum', 'Durum', 'Garanti Bitiş']],
        body: filteredServers.map((server, i) => [
          i + 1,
          server.server_name,
          server.hostname,
          server.ip_address,
          `${server.brand} ${server.model}`,
          server.operating_system,
          `${server.cpu_cores || '-'} Çekirdek`,
          `${server.ram_gb} GB`,
          `${server.storage_gb} GB`,
          (server as any).company_name || '-',
          (server as any).location_name || '-',
          statusOptions.find(s => s.value === server.status)?.label || server.status,
          server.warranty_end_date ? new Date(server.warranty_end_date).toLocaleDateString(locale) : '-',
        ]),
        startY: 28,
        styles: { fontSize: 6.5, cellPadding: 1.5 },
        headStyles: { fillColor: [37, 99, 235], textColor: 255, fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [245, 247, 250] },
        margin: { top: 28, left: 10, right: 10 },
      });
      pdf.save(`${t('physerver.excelFilename')}-${new Date().toISOString().split('T')[0]}.pdf`);
      showGlobalNotification.success(t('common.success'), `${filteredServers.length} ${t('physerver.exportPdfSuccess')}`);
    } catch (err) {
      showGlobalNotification.error(t('common.error'), t('physerver.exportPdfError'));
    }
  };

  const showServerDetail = (server: PhysicalServer) => {
    setSelectedServer(server);
    setShowDetailModal(true);
  };

  const showUpdateCheck = (server: PhysicalServer) => {
    setUpdateCheckServer(server);
    setShowUpdateCheckModal(true);
  };

  const getStatusColor = (status: string) => {
    return statusOptions.find(s => s.value === status)?.color || 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200';
  };

  const getCurrencySymbol = (currency: string) => {
    switch (currency) {
      case 'USD': return '$';
      case 'EUR': return '€';
      case 'TRY': return '₺';
      default: return '₺';
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">{t('physerver.title')}</h1>
          <p className="text-gray-600 dark:text-gray-400 text-sm sm:text-base">{t('physerver.subtitle')}</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
          <button onClick={exportToExcel} className="bg-green-600 text-white px-3 sm:px-4 py-2.5 rounded-lg flex items-center justify-center gap-2 hover:bg-green-700 text-sm min-h-[44px]">
            <Download className="w-4 h-4" />
            <span className="hidden sm:inline">Excel</span>
          </button>
          <button onClick={exportToPDF} className="bg-red-600 text-white px-3 sm:px-4 py-2.5 rounded-lg flex items-center justify-center gap-2 hover:bg-red-700 text-sm min-h-[44px]">
            <FileText className="w-4 h-4" />
            <span className="hidden sm:inline">PDF</span>
          </button>
          <button
            onClick={handleAdd}
            className="flex items-center justify-center px-4 py-3 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors min-h-[44px] text-sm sm:text-base w-full sm:w-auto"
          >
            <Plus className="w-4 h-4 mr-2" />
            {t('physerver.newServer')}
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-6 mb-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-3 sm:p-6 min-h-[80px] sm:min-h-[100px]">
          <div className="flex flex-col sm:flex-row items-center justify-between h-full">
            <div>
              <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">{t('physerver.statTotalServers')}</p>
              <p className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-gray-100">{serversData.length}</p>
            </div>
            <div className="w-8 h-8 sm:w-12 sm:h-12 bg-primary/20 rounded-lg flex items-center justify-center mt-2 sm:mt-0">
              <Server className="w-4 h-4 sm:w-6 sm:h-6 text-primary" />
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-3 sm:p-6 min-h-[80px] sm:min-h-[100px]">
          <div className="flex flex-col sm:flex-row items-center justify-between h-full">
            <div>
              <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">{t('physerver.statActive')}</p>
              <p className="text-lg sm:text-2xl font-bold text-green-600">
                {serversData.filter(s => s.status === 'active').length}
              </p>
            </div>
            <div className="w-8 h-8 sm:w-12 sm:h-12 bg-green-100 rounded-lg flex items-center justify-center mt-2 sm:mt-0">
              <CheckCircle className="w-4 h-4 sm:w-6 sm:h-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-3 sm:p-6 min-h-[80px] sm:min-h-[100px]">
          <div className="flex flex-col sm:flex-row items-center justify-between h-full">
            <div>
              <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">{t('physerver.statTotalRAM')}</p>
              <p className="text-lg sm:text-2xl font-bold text-purple-600">
                {serversData.reduce((sum, s) => sum + s.ram_gb, 0)} GB
              </p>
            </div>
            <div className="w-8 h-8 sm:w-12 sm:h-12 bg-purple-100 rounded-lg flex items-center justify-center mt-2 sm:mt-0">
              <MemoryStick className="w-4 h-4 sm:w-6 sm:h-6 text-purple-600" />
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-3 sm:p-6 min-h-[80px] sm:min-h-[100px]">
          <div className="flex flex-col sm:flex-row items-center justify-between h-full">
            <div>
              <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">{t('physerver.statTotalStorage')}</p>
              <p className="text-lg sm:text-2xl font-bold text-orange-600">
                {(serversData.reduce((sum, s) => sum + s.storage_gb, 0) / 1000).toFixed(1)} TB
              </p>
            </div>
            <div className="w-8 h-8 sm:w-12 sm:h-12 bg-orange-100 rounded-lg flex items-center justify-center mt-2 sm:mt-0">
              <HardDrive className="w-4 h-4 sm:w-6 sm:h-6 text-orange-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 mb-6 space-y-3">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder={t('physerver.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent w-full bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
            />
          </div>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100">
            <option value="all">{t('physerver.allStatuses')}</option>
            {statusOptions.map(status => (
              <option key={status.value} value={status.value}>{status.label}</option>
            ))}
          </select>
          <select value={filterServerType} onChange={(e) => setFilterServerType(e.target.value)} className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100">
            <option value="all">{t('physerver.allServerTypes')}</option>
            {serverTypeOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <select value={filterBrand} onChange={(e) => setFilterBrand(e.target.value)} className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100">
            <option value="all">{t('physerver.allBrands')}</option>
            {uniqueBrands.map(b => <option key={b} value={b}>{b}</option>)}
          </select>
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          <select value={filterCompany} onChange={(e) => setFilterCompany(parseInt(e.target.value))} className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100">
            <option value={0}>{t('physerver.allCompanies')}</option>
            {companiesData.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <select value={filterLocation} onChange={(e) => setFilterLocation(parseInt(e.target.value))} className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100">
            <option value={0}>{t('physerver.allLocations')}</option>
            {locationsData.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
          </select>
        </div>
      </div>

      {/* Servers Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700 dark:bg-gray-900">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('physerver.colServerName')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('physerver.colHostname')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('physerver.colIPAddress')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('physerver.colBrandModel')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('physerver.colCompany')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('physerver.colLocation')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('physerver.colResources')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('physerver.colStatus')}
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('physerver.colActions')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {filteredServers.map((server) => {
                // Calculate resource usage
                const vmsOnServer = virtualServersData.filter(vm => vm.physical_server_id === server.id);
                const allocatedCPU = vmsOnServer.reduce((sum, vm) => sum + (vm.vcpu_count || 0), 0);
                const allocatedRAM = vmsOnServer.reduce((sum, vm) => sum + (vm.ram_gb || 0), 0);
                // VMs with external storage unit don't consume the physical server's local disk
                const allocatedStorage = vmsOnServer.reduce((sum, vm) => sum + (vm.storage_unit_id ? 0 : (vm.storage_gb || 0)), 0);

                const remainingCPU = (server.cpu_cores || 0) - allocatedCPU;
                const remainingRAM = server.ram_gb - allocatedRAM;
                const remainingStorage = server.storage_gb - allocatedStorage;

                return (
                  <tr key={server.id} className="hover:bg-gray-50 dark:bg-gray-700 dark:hover:bg-gray-700/50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10 bg-primary/20 dark:bg-primary/30 rounded-lg flex items-center justify-center">
                          <Server className="h-5 w-5 text-primary dark:text-primary" />
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{server.server_name}</div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">{server.operating_system}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 dark:text-gray-100">{server.hostname}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 dark:text-gray-100 font-mono">{server.ip_address}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 dark:text-gray-100">{server.brand}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">{server.model}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 dark:text-gray-100">{server.company_name || '-'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 dark:text-gray-100">{server.location_name || '-'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-xs space-y-1">
                        <div className={`flex items-center ${remainingCPU < (server.cpu_cores || 0) * 0.2 ? 'text-red-600 font-semibold' : 'text-gray-600 dark:text-gray-400'}`}>
                          <Cpu className="w-3 h-3 mr-1" />
                          {remainingCPU}/{server.cpu_cores || 0} vCPU
                        </div>
                        <div className={`flex items-center ${remainingRAM < server.ram_gb * 0.2 ? 'text-red-600 font-semibold' : 'text-gray-600 dark:text-gray-400'}`}>
                          <MemoryStick className="w-3 h-3 mr-1" />
                          {remainingRAM}/{server.ram_gb} GB
                        </div>
                        <div className={`flex items-center ${remainingStorage < server.storage_gb * 0.2 ? 'text-red-600 font-semibold' : 'text-gray-600 dark:text-gray-400'}`}>
                          <HardDrive className="w-3 h-3 mr-1" />
                          {remainingStorage}/{server.storage_gb} GB
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(server.status)}`}>
                        {statusOptions.find(s => s.value === server.status)?.label}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => showUpdateCheck(server)}
                          className="text-gray-400 hover:text-orange-500 dark:hover:text-orange-400 transition-colors"
                          title="Güncelleme Kontrolü"
                        >
                          <RefreshCw className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => showServerDetail(server)}
                          className="text-gray-400 hover:text-primary dark:hover:text-primary transition-colors"
                          title={t('physerver.titleView')}
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDuplicate(server)}
                          className="text-gray-400 hover:text-green-600 dark:hover:text-green-400 transition-colors"
                          title={t('physerver.titleDuplicate')}
                        >
                          <Copy className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleEdit(server)}
                          className="text-gray-400 hover:text-primary dark:hover:text-primary transition-colors"
                          title={t('physerver.titleEdit')}
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(server.id)}
                          className="text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                          title={t('physerver.titleDelete')}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {filteredServers.length === 0 && (
          <div className="text-center py-12">
            <Server className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">{t('physerver.notFound')}</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{t('physerver.notFoundDesc')}</p>
          </div>
        )}
      </div>

      {/* Server Modal */}
      <Modal isOpen={showModal} onClose={() => setShowModal(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-4xl max-h-[95vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {editingServer ? t('physerver.modalTitleEdit') : t('physerver.modalTitleNew')}
              </h2>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('physerver.fieldServerName')}
                  </label>
                  <input
                    type="text"
                    value={serverData.server_name}
                    onChange={(e) => setServerData({...serverData, server_name: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    placeholder="SRV-WEB-01"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('physerver.fieldHostname')}
                  </label>
                  <input
                    type="text"
                    value={serverData.hostname}
                    onChange={(e) => setServerData({...serverData, hostname: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    placeholder="web01.company.local"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('physerver.fieldIPAddress')}
                  </label>
                  <input
                    type="text"
                    value={serverData.ip_address}
                    onChange={(e) => setServerData({...serverData, ip_address: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    placeholder="192.168.1.10"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('physerver.fieldServerType')}
                  </label>
                  <select
                    value={serverData.server_type}
                    onChange={(e) => setServerData({...serverData, server_type: e.target.value as PhysicalServer['server_type']})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  >
                    {serverTypeOptions.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('physerver.fieldBrand')}
                  </label>
                  <input
                    type="text"
                    value={serverData.brand}
                    onChange={(e) => setServerData({...serverData, brand: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    placeholder="HPE"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('physerver.fieldModel')}
                  </label>
                  <input
                    type="text"
                    value={serverData.model}
                    onChange={(e) => setServerData({...serverData, model: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    placeholder="ProLiant DL380 Gen10"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('physerver.fieldSerialNo')}
                  </label>
                  <input
                    type="text"
                    value={serverData.serial_number}
                    onChange={(e) => setServerData({...serverData, serial_number: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    placeholder="ABC123456789"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('physerver.fieldRAM')}
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={serverData.ram_gb}
                    onChange={(e) => setServerData({...serverData, ram_gb: parseInt(e.target.value) || 16})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('physerver.fieldStorage')}
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={serverData.storage_gb}
                    onChange={(e) => setServerData({...serverData, storage_gb: parseInt(e.target.value) || 1000})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('physerver.fieldProcessor')}
                  </label>
                  <input
                    type="text"
                    value={serverData.processor}
                    onChange={(e) => setServerData({...serverData, processor: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    placeholder="Intel Xeon E5-2620 v4"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('physerver.fieldCPUCores')}
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={serverData.cpu_cores}
                    onChange={(e) => setServerData({...serverData, cpu_cores: parseInt(e.target.value) || 0})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    placeholder="16"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('physerver.fieldOS')}
                  </label>
                  <input
                    type="text"
                    value={serverData.operating_system}
                    onChange={(e) => setServerData({...serverData, operating_system: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    placeholder="Windows Server 2019"
                  />
                </div>
              </div>

              {/* İkinci Grid Section - Lokasyon ve Şirket Bilgileri */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('physerver.fieldCompany')}
                  </label>
                  <select
                    value={serverData.company_id}
                    onChange={(e) => setServerData({...serverData, company_id: parseInt(e.target.value)})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  >
                    <option value={0}>{t('physerver.selectCompany')}</option>
                    {companiesData.map((company) => (
                      <option key={company.id} value={company.id}>
                        {company.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('physerver.fieldLocation')}
                  </label>
                  <select
                    value={serverData.location_id}
                    onChange={(e) => setServerData({...serverData, location_id: parseInt(e.target.value)})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  >
                    <option value={0}>{t('physerver.selectLocation')}</option>
                    {locationsData.map((location) => (
                      <option key={location.id} value={location.id}>
                        {location.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('physerver.fieldPurchaseDate')}
                  </label>
                  <input
                    type="date"
                    value={serverData.purchase_date}
                    onChange={(e) => setServerData({...serverData, purchase_date: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('physerver.fieldWarrantyEnd')}
                  </label>
                  <input
                    type="date"
                    value={serverData.warranty_end_date}
                    onChange={(e) => setServerData({...serverData, warranty_end_date: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('physerver.fieldAssignedAdmin')}
                  </label>
                  <select
                    value={serverData.assigned_admin_id}
                    onChange={(e) => setServerData({...serverData, assigned_admin_id: parseInt(e.target.value) || null})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  >
                    <option value={0}>{t('physerver.selectAdmin')}</option>
                    {usersData.map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.name} ({user.role})
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end space-x-3">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:bg-gray-700 dark:hover:bg-gray-700 transition-colors"
              >
                {t('physerver.btnCancel')}
              </button>
              <button
                onClick={handleSave}
                className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark transition-colors"
              >
                {editingServer ? t('physerver.btnUpdate') : t('physerver.btnSave')}
              </button>
            </div>
          </div>
        </Modal>

      {/* Server Update Check Modal */}
      {updateCheckServer && (
        <ServerUpdateCheckModal
          isOpen={showUpdateCheckModal}
          onClose={() => { setShowUpdateCheckModal(false); setUpdateCheckServer(null); }}
          serverType="physical"
          serverId={updateCheckServer.id}
          serverName={updateCheckServer.server_name}
          adminUsers={usersData}
        />
      )}

      {/* Server Detail Modal */}
      <Modal isOpen={showDetailModal} onClose={() => setShowDetailModal(false)}>
          {selectedServer && (
          <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    {t('physerver.detailTitle')}
                  </h2>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    {selectedServer.server_name} - {selectedServer.hostname}
                  </p>
                </div>
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="text-gray-400 hover:text-gray-600 dark:text-gray-400"
                >
                  ✕
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">{t('physerver.detailSectionServer')}</h3>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">{t('physerver.detailServerName')}</span>
                      <span className="font-medium text-gray-900 dark:text-gray-100">{selectedServer.server_name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">{t('physerver.detailHostname')}</span>
                      <span className="font-medium text-gray-900 dark:text-gray-100">{selectedServer.hostname}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">{t('physerver.detailIPAddress')}</span>
                      <span className="font-medium text-gray-900 dark:text-gray-100">{selectedServer.ip_address}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">{t('physerver.detailBrandModel')}</span>
                      <span className="font-medium text-gray-900 dark:text-gray-100">{selectedServer.brand} {selectedServer.model}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">{t('physerver.detailOS')}</span>
                      <span className="font-medium text-gray-900 dark:text-gray-100">{selectedServer.operating_system}</span>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">{t('physerver.detailSectionHardware')}</h3>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">{t('physerver.detailProcessor')}</span>
                      <span className="font-medium text-gray-900 dark:text-gray-100">{selectedServer.processor}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">{t('physerver.detailRAM')}</span>
                      <span className="font-medium text-gray-900 dark:text-gray-100">{selectedServer.ram_gb} GB</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">{t('physerver.detailStorage')}</span>
                      <span className="font-medium text-gray-900 dark:text-gray-100">{selectedServer.storage_gb} GB</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">{t('physerver.detailPowerConsumption')}</span>
                      <span className="font-medium text-gray-900 dark:text-gray-100">{selectedServer.power_consumption_watts}W</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">{t('physerver.detailUptime')}</span>
                      <span className="font-medium text-gray-900 dark:text-gray-100">{t('physerver.detailUptimeDays', { days: selectedServer.uptime_days })}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Virtual Servers and Resource Usage */}
              {(() => {
                const vmsOnThisServer = virtualServersData.filter(vm => vm.physical_server_id === selectedServer.id);
                const totalAllocatedVCPU = vmsOnThisServer.reduce((sum, vm) => sum + (vm.vcpu_count || 0), 0);
                const totalAllocatedRAM = vmsOnThisServer.reduce((sum, vm) => sum + (vm.ram_gb || 0), 0);
                // VMs with external storage unit don't consume the physical server's local disk
                const totalAllocatedStorage = vmsOnThisServer.reduce((sum, vm) => sum + (vm.storage_unit_id ? 0 : (vm.storage_gb || 0)), 0);

                const physicalCores = selectedServer.cpu_cores || 0;
                const remainingCores = physicalCores - totalAllocatedVCPU;
                const remainingRAM = selectedServer.ram_gb - totalAllocatedRAM;
                const remainingStorage = selectedServer.storage_gb - totalAllocatedStorage;

                return (
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
                      {t('physerver.detailSectionVMs')}
                    </h3>

                    {/* Resource Summary */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                      <div className="bg-primary/10 dark:bg-primary/20 p-4 rounded-lg border border-primary/30 dark:border-primary-dark">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('physerver.vcpuAllocation')}</span>
                          <Cpu className="w-4 h-4 text-primary" />
                        </div>
                        <div className="space-y-1">
                          <div className="flex justify-between text-xs">
                            <span className="text-gray-600 dark:text-gray-400">{t('physerver.allocated')}</span>
                            <span className="font-medium text-gray-900 dark:text-gray-100">{totalAllocatedVCPU} vCore</span>
                          </div>
                          <div className="flex justify-between text-xs">
                            <span className="text-gray-600 dark:text-gray-400">{t('physerver.remaining')}</span>
                            <span className={`font-medium ${remainingCores < physicalCores * 0.2 ? 'text-red-600' : 'text-primary'}`}>
                              {remainingCores}/{physicalCores} vCore
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg border border-purple-200 dark:border-purple-800">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('physerver.ramAllocation')}</span>
                          <MemoryStick className="w-4 h-4 text-purple-600" />
                        </div>
                        <div className="space-y-1">
                          <div className="flex justify-between text-xs">
                            <span className="text-gray-600 dark:text-gray-400">{t('physerver.allocated')}</span>
                            <span className="font-medium text-gray-900 dark:text-gray-100">{totalAllocatedRAM} GB</span>
                          </div>
                          <div className="flex justify-between text-xs">
                            <span className="text-gray-600 dark:text-gray-400">{t('physerver.remaining')}</span>
                            <span className={`font-medium ${remainingRAM < selectedServer.ram_gb * 0.2 ? 'text-red-600' : 'text-purple-600'}`}>
                              {remainingRAM} GB ({((remainingRAM / selectedServer.ram_gb) * 100).toFixed(0)}%)
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="bg-orange-50 dark:bg-orange-900/20 p-4 rounded-lg border border-orange-200 dark:border-orange-800">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('physerver.diskAllocation')}</span>
                          <HardDrive className="w-4 h-4 text-orange-600" />
                        </div>
                        <div className="space-y-1">
                          <div className="flex justify-between text-xs">
                            <span className="text-gray-600 dark:text-gray-400">{t('physerver.allocated')}</span>
                            <span className="font-medium text-gray-900 dark:text-gray-100">{totalAllocatedStorage} GB</span>
                          </div>
                          <div className="flex justify-between text-xs">
                            <span className="text-gray-600 dark:text-gray-400">{t('physerver.remaining')}</span>
                            <span className={`font-medium ${remainingStorage < selectedServer.storage_gb * 0.2 ? 'text-red-600' : 'text-orange-600'}`}>
                              {remainingStorage} GB ({((remainingStorage / selectedServer.storage_gb) * 100).toFixed(0)}%)
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* VM List */}
                    {vmsOnThisServer.length > 0 ? (
                      <div className="space-y-2">
                        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                          {t('physerver.connectedVMs', { count: vmsOnThisServer.length })}
                        </h4>
                        <div className="space-y-2 max-h-48 overflow-y-auto">
                          {vmsOnThisServer.map((vm) => (
                            <div key={vm.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg text-sm">
                              <div className="flex-1">
                                <div className="font-medium text-gray-900 dark:text-gray-100">{vm.vm_name}</div>
                                <div className="text-xs text-gray-500 dark:text-gray-400">{vm.hostname}</div>
                              </div>
                              <div className="flex gap-4 text-xs text-gray-600 dark:text-gray-400">
                                <span className="flex items-center gap-1">
                                  <Cpu className="w-3 h-3" />
                                  {vm.vcpu_count}
                                </span>
                                <span className="flex items-center gap-1">
                                  <MemoryStick className="w-3 h-3" />
                                  {vm.ram_gb}GB
                                </span>
                                <span className={`flex items-center gap-1 ${vm.storage_unit_id ? 'text-teal-600 dark:text-teal-400' : ''}`} title={vm.storage_unit_id ? 'Harici depolama ünitesi kullanıyor' : 'Yerel disk'}>
                                  <HardDrive className="w-3 h-3" />
                                  {vm.storage_unit_id ? 'Harici' : `${vm.storage_gb}GB`}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-8 text-gray-500 dark:text-gray-400 text-sm">
                        {t('physerver.noVMs')}
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>
          )}
        </Modal>
    </div>
  );
};

export default PhysicalServersPage;